import { useLocalStorageState } from 'ahooks';
import { Form, FormInstance } from 'antd';
import { isEmpty } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppConfig } from '@/config/context';
import { getProductId, isAndroid, isFlutter, isHarmonyOS } from '@/infrastructure/product-info';
import { getCompareDateValue, searchValueDeserializer } from '@/infrastructure/utils/issue';
import { toArray } from '@/infrastructure/utils/to-array';
import { FormEntry, FormType, IFormRef, QueryForm } from '@/presentation/components/form';
import { ANR_LAG_LIMIT_DAYS, DEFAULT_MONITOR_LIMIT_DAYS } from '@/presentation/components/form/components/time-range';
import {
  CHARACTER_MATCH_ARRAY_OPTIONS,
  CHARACTER_MATCH_OPTIONS,
  MatchMode, NUMBER_MATCH_OPTIONS,
  STACK_CHARACTER_MATCH_OPTIONS,
  VERSION_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import { FilterEntry, FormColumnGroup } from '@/presentation/components/form/types';
import {
  getShowSwitchStatus,
  onChangeAppVersion,
  tokenizeValue,
} from '@/presentation/components/form/utils';
import { hintText } from '@/presentation/components/hint-text/hintText';
import {
  enumFilterTips,
  numberFilterTips,
  stringArrayFilterTips,
  versionFilterTips,
} from '@/presentation/components/issue-list/components/tips';
import { SEARCH_VALUES } from '@/presentation/config/storage';
import { SearchValueStorageType } from '@/presentation/config/type';
import { FIELD_ALL, FIELD_LABEL, FieldKey, Zone } from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { TimeType } from '@/proto/metric';
import { CompareFieldList, FieldList } from '@/proto/token';

interface IProps {
  onFinish: (value: Record<string, any>) => void;
  entry: FilterEntry;
  initialValue?: CompareFieldList & { timeRange: string[], isValueInit?: boolean };
  onValueInited?: (value: Record<string, any>) => void;
  form: FormInstance;
  type?: string;
  tokenInitValues: FieldList & { isValueInit?: boolean };
}

export const getDropFrameFormItems = (props: {
  type?: string,
  showSwitch: boolean;
  entry: FilterEntry;
  config: any;
}) => {
  const entries: FormEntry[] = [
    props.type !== 'anr' ? {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      labelString: FIELD_LABEL[FieldKey.TimeRange],
      label: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'issues',
      deleteable: false,
      limitDays: DEFAULT_MONITOR_LIMIT_DAYS,
      showTime: props.entry === FilterEntry.DropFrameDetail || props.entry === FilterEntry.DropFrameList,
      initTimeWindow: TimeType.MINUTE5,
      hiddenTimeWindow: props.entry !== FilterEntry.DropFrameList,
      isIssuesTimeWindow: props.entry === FilterEntry.DropFrameList,
    } : {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      labelString: FIELD_LABEL[FieldKey.TimeRange],
      label: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'analytics',
      timeQuickOptions: [{
        label: '自定义',
        value: 'custom',
      },
      {
        label: '近1小时',
        value: '近1小时',
      },
      {
        label: '今天',
        value: '今天',
      },
      {
        label: '昨天',
        value: '昨天',
      },
      {
        label: '近7天',
        value: '近7天',
      }],
      deleteable: false,
      hiddenTimeWindow: true,
      limitDays: ANR_LAG_LIMIT_DAYS,
      showTime: true,
      initTimeWindow: TimeType.DAY,
      isIssuesTimeWindow: false,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAppVersion,
      label: hintText(FIELD_LABEL[FieldKey.AppVersion], versionFilterTips),
      labelString: FIELD_LABEL[FieldKey.AppVersion],
      fieldKey: FieldKey.AppVersion,
      fieldScope: Zone.Lag,
      scope: 'issues',
      mode: 'tags',
      deleteable: false,
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
        style: {
          width: 106,
        },
      },
      valueSelectProps: {
        style: {
          width: 450,
        },
      },
      modeOptions: VERSION_MATCH_OPTIONS,
    },
    {
      type: FormType.Select,
      name: FieldKey.StandardVersionMode,
      label: hintText(FIELD_LABEL[FieldKey.AppVersionMode], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.AppVersionMode],
      fieldKey: FieldKey.AppVersionMode,
      fieldScope: Zone.Lag,
      mode: 'multiple',
      initialValue: [FIELD_ALL],
      group: FormColumnGroup.ApplicationInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: FieldKey.StandardGround,
      label: hintText(FIELD_LABEL[FieldKey.Ground], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.Ground],
      fieldKey: FieldKey.Ground,
      fieldScope: Zone.Lag,
      mode: 'multiple',
      initialValue: [FIELD_ALL],
      group: FormColumnGroup.ContextInfo,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardScene,
      label: FIELD_LABEL[FieldKey.Scene],
      fieldKey: FieldKey.Scene,
      fieldScope: Zone.Lag,
      dropdownMatchSelectWidth: 500,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.ContextInfo,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardBrand,
      label: FIELD_LABEL[FieldKey.Brand],
      disabled: !isAndroid(),
      mode: 'tags',
      fieldKey: FieldKey.Brand,
      fieldScope: Zone.Lag,
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.DeviceInfo,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardProcessName,
      label: FIELD_LABEL[FieldKey.ProcessName],
      fieldKey: FieldKey.ProcessName,
      fieldScope: Zone.Lag,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      disabled: !isAndroid(),
      compareDisabled: !isAndroid(),
      group: FormColumnGroup.exceptionInfo,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardOsVersion,
      label: hintText(FIELD_LABEL[FieldKey.OsVersion], versionFilterTips),
      labelString: FIELD_LABEL[FieldKey.OsVersion],
      fieldKey: FieldKey.OsVersion,
      fieldScope: Zone.Lag,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: VERSION_MATCH_OPTIONS,
      group: FormColumnGroup.DeviceInfo,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardSdkVersion,
      group: FormColumnGroup.ApplicationInfo,
      label: hintText(FIELD_LABEL[FieldKey.SdkVersion], versionFilterTips),
      labelString: FIELD_LABEL[FieldKey.SdkVersion],
      fieldKey: FieldKey.SdkVersion,
      fieldScope: Zone.Lag,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: VERSION_MATCH_OPTIONS,
      dropdownMatchSelectWidth: 300,
      rangeProps: {
        dropDownWidth: 220,
      },
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardModel,
      group: FormColumnGroup.DeviceInfo,
      label: FIELD_LABEL[FieldKey.Model],
      fieldKey: FieldKey.Model,
      fieldScope: Zone.Lag,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.Platform,
      group: FormColumnGroup.DeviceInfo,
      label: FIELD_LABEL[FieldKey.Platform],
      fieldKey: FieldKey.Platform,
      fieldScope: Zone.Lag,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      disabled: !isFlutter(),
      compareDisabled: !isFlutter(),
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAccountId,
      group: FormColumnGroup.UserInfo,
      isSplit: true,
      label: '用户ID',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardQimei,
      group: FormColumnGroup.DeviceInfo,
      label: '设备ID',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardLaunchId,
      group: FormColumnGroup.ContextInfo,
      label: FIELD_LABEL[FieldKey.LaunchId],
      isSplit: true,
      labelString: '启动ID',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardProcessLaunchId,
      group: FormColumnGroup.exceptionInfo,
      label: FIELD_LABEL[FieldKey.ProcessLaunchId],
      isSplit: true,
      labelString: '进程启动ID',
      disabled: !isAndroid(),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardClientIdentify,
      group: FormColumnGroup.exceptionInfo,
      label: '消息ID',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardFeature,
      group: FormColumnGroup.exceptionInfo,
      label: 'Issue ID',
      disabled: props.entry !== FilterEntry.DropFrameList,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardFuncName,
      group: FormColumnGroup.exceptionInfo,
      label: '问题特征',
      disabled: props.entry !== FilterEntry.DropFrameList,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardStack,
      group: FormColumnGroup.exceptionInfo,
      label: '卡顿堆栈',
      labelString: '卡顿堆栈',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.TOKENIZATION,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.TOKENIZATION,
      },
      convertValue: tokenizeValue,
      isSplit: true,
      modeOptions: STACK_CHARACTER_MATCH_OPTIONS,
      disabled: props.entry !== FilterEntry.DropFrameDetail,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardShiplyTag,
      group: FormColumnGroup.Custom,
      label: hintText(FIELD_LABEL[FieldKey.ShiplyTag], stringArrayFilterTips),
      labelString: FIELD_LABEL[FieldKey.ShiplyTag],
      fieldKey: FieldKey.TestTag,
      fieldScope: Zone.Lag,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      disabled: !props.config.enableShiplyAbTestTag || props.entry !== FilterEntry.DropFrameList,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAbTestTag,
      group: FormColumnGroup.Custom,
      label: hintText(FIELD_LABEL[FieldKey.AbTestTag], stringArrayFilterTips),
      labelString: FIELD_LABEL[FieldKey.AbTestTag],
      fieldKey: FieldKey.AbTestTagLine,
      fieldScope: Zone.Lag,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      disabled: props.entry !== FilterEntry.DropFrameList,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardTagsInFilter,
      group: FormColumnGroup.Tag,
      label: hintText(FIELD_LABEL[FieldKey.TagsInFilter], stringArrayFilterTips),
      labelString: FIELD_LABEL[FieldKey.TagsInFilter],
      fieldScope: Zone.Lag,
      fieldKey: FieldKey.TagsInFilterUnderline,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      compareDisabled: true,
      disabled: props.entry !== FilterEntry.DropFrameList,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardBundleId,
      group: FormColumnGroup.ApplicationInfo,
      isSplit: true,
      label: FIELD_LABEL[FieldKey.BundleId],
      fieldKey: FieldKey.BundleId,
      fieldScope: Zone.Lag,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardChannelId,
      label: '渠道',
      isSplit: true,
      fieldKey: FieldKey.ChannelId,
      fieldScope: Zone.Lag,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardMonitoredThreadName,
      group: FormColumnGroup.exceptionInfo,
      label: FIELD_LABEL[FieldKey.MonitoredThreadName],
      fieldKey: FieldKey.MonitoredThreadName,
      fieldScope: Zone.Lag,
      disabled: isHarmonyOS(),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      compareDisabled: true,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: FieldKey.StandardSystemStack,
      group: FormColumnGroup.exceptionInfo,
      label: hintText(FIELD_LABEL[FieldKey.SystemStack], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.SystemStack],
      fieldKey: FieldKey.SystemStack,
      fieldScope: Zone.Lag,
      disabled: props.entry !== FilterEntry.DropFrameList,
      compareDisabled: true,
      initialValue: [FIELD_ALL],
      mode: 'multiple',
      initialDeleted: true,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.StandardTimeCost,
      group: FormColumnGroup.exceptionInfo,
      label: hintText('卡顿耗时（毫秒）', numberFilterTips),
      labelString: '卡顿耗时（毫秒）',
      options: NUMBER_MATCH_OPTIONS,
      disabled: props.entry !== FilterEntry.DropFrameList,
      initialDeleted: true,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.StandardLaunchDuration,
      group: FormColumnGroup.exceptionInfo,
      label: hintText('使用时长（秒）', numberFilterTips),
      labelString: '使用时长（秒）',
      options: NUMBER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardDetectStrategy,
      group: FormColumnGroup.exceptionInfo,
      label: '监控粒度',
      disabled: !isAndroid(),
      fieldKey: FieldKey.DetectStrategy,
      fieldScope: Zone.Lag,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardRomName',
      initialDeleted: true,
      group: FormColumnGroup.DeviceInfo,
      disabled: !isAndroid(),
      label: 'ROM',
      labelString: 'ROM',
      fieldKey: FieldKey.Rom,
      fieldScope: Zone.Lag,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
    },
    {
      type: FormType.TdsArea,
      name: FieldKey.StandardArea,
      label: '地域',
      labelString: '地域',
      fieldScope: Zone.Lag,
      compareDisabled: true,
      initialDeleted: true,
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.TdsArea,
      name: FieldKey.StandardArea,
      label: '地域',
      labelString: '地域',
      fieldScope: Zone.Lag,
      compareDisabled: true,
      initialDeleted: true,
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.TdsArea,
      name: FieldKey.StandardArea,
      label: '地域',
      labelString: '地域',
      fieldScope: Zone.Lag,
      compareDisabled: true,
      initialDeleted: true,
    },
    {
      type: FormType.CustomFields,
      name: FieldKey.UserCustom,
      group: FormColumnGroup.Custom,
      label: '自定义维度',
      disabled: props.entry !== FilterEntry.DropFrameList,
      initialDeleted: true,
    },
    {
      type: FormType.CustomKeys,
      name: FieldKey.BizExtendInfo,
      group: FormColumnGroup.Custom,
      label: '自定义字段',
      initialDeleted: true,
    },
    {
      type: FormType.Input,
      name: 'anrIssueId',
      group: FormColumnGroup.exceptionInfo,
      label: 'ANR Issue ID',
      disabled: props.type !== 'anr',
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: 'showNew',
      label: hintText('只显示新增问题', enumFilterTips),
      labelString: '只显示新增问题',
      disabled: !props.showSwitch || props.entry !== FilterEntry.DropFrameList,
      deleteable: false,
      mode: 'single',
      fieldKey: FieldKey.ShowNew,
      fieldScope: Zone.Lag,
      initialValue: false,
      compareDisabled: true,
      initialDeleted: true,
    },

  ];

  return entries;
};

export const ProblemForm: FC<IProps> = observer((props: IProps) => {
  const { tokenInitValues } = props;
  const [topForm] = Form.useForm();
  const tableRef = useRef<IFormRef>(null);
  const form = props.form ? props.form : topForm;
  const [showSwitch, setShowSwitch] = useState(false);
  const config = useAppConfig();
  const [searchValues, setSearchValues] = useLocalStorageState<SearchValueStorageType>(`${SEARCH_VALUES}-${props.entry}-${getProductId()}`, {
    defaultValue: [],
  });

  useEffect(() => {
    const { type, entry } = props;
    if (!isEmpty(tokenInitValues)) {
      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(getDropFrameFormItems({
        type,
        showSwitch,
        entry,
        config,
      }), tokenInitValues)?.then(() => {
        const {
          isValueInit = true,
        } = tokenInitValues;
        form.setFieldsValue({
          ...tokenInitValues,
        });
        if (isValueInit) {
          props.onValueInited?.(form.getFieldsValue());
        }
        // 初始化显示 【只显示新增问题】字段
        setShowSwitch(getShowSwitchStatus({
          values: toArray(tokenInitValues?.[FieldKey.StandardAppVersion]?.values ?? []),
          operator: tokenInitValues?.[FieldKey.StandardAppVersion]?.mode ?? '',
        }));
      });
    }
  }, [tokenInitValues]);

  useEffect(() => {
    const { initialValue } = props;
    if (!isEmpty(initialValue)) {
      const {
        isValueInit = true,
      } = initialValue;
      form.setFieldsValue({
        ...initialValue,
      });
      if (isValueInit) {
        props.onValueInited?.(form.getFieldsValue());
      }
      // 初始化显示 【只显示新增问题】字段
      setShowSwitch(getShowSwitchStatus({
        values: toArray(initialValue?.[FieldKey.StandardAppVersion]?.values ?? []),
        operator: initialValue?.[FieldKey.StandardAppVersion]?.mode ?? '',
      }));
    }
  }, [props.initialValue]);

  const onFinish = (value: Record<string, any>, compareSwitch?: boolean) => {
    const { costTimeAvg, costTimeAvgCompare, ...rest } = value;

    const compareDateValue = getCompareDateValue(value, compareSwitch);
    if (compareSwitch) {
      form.setFieldsValue(compareDateValue);
    }
    // const isExpiredTimeRange = initJudgmentTimeRange(value);
    // if (isExpiredTimeRange) return;

    if (costTimeAvg || costTimeAvgCompare) {
      props.onFinish({
        costTimeAvgMin: costTimeAvg?.value[0],
        costTimeAvgMax: costTimeAvg?.value[1],
        costTimeAvgMinCompare: costTimeAvgCompare?.value[0],
        costTimeAvgMaxCompare: costTimeAvgCompare?.value[1],
        ...rest,
        ...compareDateValue,
      });
    } else {
      props.onFinish({ ...value, ...compareDateValue });
    }
  };

  return <QueryForm
    ref={tableRef}
    formProps={{
      isKeyPressSubmit: false,
    }}
    form={form}
    entries={getDropFrameFormItems({
      type: props.type,
      showSwitch,
      entry: props.entry,
      config,
    })}
    cachePrefix={'prefix2'}
    onFinish={onFinish}
    onChange={async (__, fields) => onChangeAppVersion({
      values: toArray(fields?.[FieldKey.StandardAppVersion]?.values ?? []),
      operator: fields?.[FieldKey.StandardAppVersion]?.mode ?? '',
    }, setShowSwitch)}
    onReset={() => setShowSwitch(false)}
    pageEntry={props.entry}
    issueType={IssueType.ISSUE_TYPE_LAG}
    hasCompare={props.entry === FilterEntry.DropFrameList}
    defaultValues={props.initialValue}
    isSaveSearchValues={true}
    searchValues={searchValueDeserializer(searchValues)}
    setSearchValues={setSearchValues}
    renderByGroup
  />;
});
